import { useState } from 'react';
import { CalendarClock, Loader2 } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ElectionStage,
  ElectionOutcomeResult,
  electionStageLabel,
  electionOutcomeResultLabel,
  type ElectionOutcomeDto,
} from '@signalvote/shared';
import { useCampaignStore } from '@/stores';
import { useAuth } from '@/contexts/AuthContext';
import { getCampaignTodayString } from '@/lib/calendarUtils';
import {
  financeFormSelectClass,
  financeFormLabelClass,
} from './shared/financeControlClasses';
import {
  useCampaignElectionOutcomes,
  useRecordElectionOutcome,
  ElectionOutcomeRecordError,
} from '@/hooks/queries/useCampaignElectionOutcomes';
import { formatDateTime } from './utils';

// Every stage / result the admin can record, in cycle order. Labels are resolved at the render
// boundary via the shared formatters — the engineer-facing enum identifier never reaches the UI.
const STAGES: ElectionStage[] = [
  ElectionStage.Primary,
  ElectionStage.PrimaryRunoff,
  ElectionStage.General,
  ElectionStage.GeneralRunoff,
  ElectionStage.SingleElection,
];

const RESULTS: ElectionOutcomeResult[] = [
  ElectionOutcomeResult.Advanced,
  ElectionOutcomeResult.WentToRunoff,
  ElectionOutcomeResult.Won,
  ElectionOutcomeResult.Lost,
  ElectionOutcomeResult.Withdrew,
];

/**
 * ADR-076 (#630) — the owner / platform-admin control to record a campaign's election outcome. It
 * lists the current per-stage outcomes and records one (stage + result picker) via the recording
 * endpoint. Choosing "Went to runoff" reveals a runoff-date input (it feeds the stage's own runoff
 * date since the #626/#629 split — primary-runoff for a primary stage, general-runoff for a general
 * one; its cutoff effect lands in the outcome-drives-cutoff ticket). The bar is the same as suspending an
 * account — owner or platform admin, not Finance-Manage — enforced by the API and mirrored here as
 * defense-in-depth. One campaign at a time.
 */
export function RecordElectionOutcome() {
  const isOwner = useCampaignStore((s) => s.isOwner());
  const { user } = useAuth();
  const canRecord = isOwner || (user?.isPlatformAdmin ?? false);

  const { data: outcomes, isLoading, isError } = useCampaignElectionOutcomes();
  const record = useRecordElectionOutcome();

  const [stage, setStage] = useState<ElectionStage | ''>('');
  const [result, setResult] = useState<ElectionOutcomeResult | ''>('');
  const [runoffDate, setRunoffDate] = useState('');

  // The same bar as recording (owner or platform admin). Defense-in-depth: the tab and the API both
  // gate this too, so a Finance-Manage treasurer never reaches a working form.
  if (!canRecord) {
    return (
      <Card data-testid="record-outcome-forbidden">
        <CardContent className="py-6 text-sm text-muted-foreground">
          Only a campaign owner or platform admin can record an election outcome.
        </CardContent>
      </Card>
    );
  }

  // The token to send is the selected stage's current version (0 when that stage is un-recorded), so
  // a change to an already-recorded stage carries the right token and the server won't false-conflict.
  const existing = stage ? outcomes?.find((o) => o.stage === stage) : undefined;
  const concurrencyVersion = existing?.concurrencyVersion ?? 0;

  const needsRunoffDate = result === ElectionOutcomeResult.WentToRunoff;
  const canSubmit =
    stage !== '' &&
    result !== '' &&
    (!needsRunoffDate || runoffDate !== '') &&
    !record.isPending;

  const recordError =
    record.error instanceof ElectionOutcomeRecordError ? record.error.message : null;

  const handleRecord = () => {
    if (stage === '' || result === '') return;
    record.mutate(
      {
        stage,
        result,
        runoffDate: needsRunoffDate ? runoffDate : null,
        concurrencyVersion,
      },
      {
        onSuccess: () => {
          setResult('');
          setRunoffDate('');
        },
      },
    );
  };

  return (
    <Card data-testid="record-outcome">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5" /> Election outcome
        </CardTitle>
        <CardDescription>
          Record what happened at each stage of this campaign's race. A terminal result (won, lost,
          or withdrew) settles the race; "went to runoff" keeps it live to the runoff date.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current per-stage outcomes */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Recorded so far</h3>
          {isLoading ? (
            <div
              className="flex items-center gap-2 text-sm text-muted-foreground"
              data-testid="current-outcomes-loading"
            >
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : isError ? (
            <div className="text-sm text-destructive">Could not load this campaign's outcomes.</div>
          ) : !outcomes || outcomes.length === 0 ? (
            <div className="text-sm text-muted-foreground" data-testid="current-outcomes-empty">
              Nothing recorded yet.
            </div>
          ) : (
            <dl className="divide-y divide-border rounded-md border border-border" data-testid="current-outcomes">
              {outcomes.map((o: ElectionOutcomeDto) => (
                <div key={o.id} className="flex items-center justify-between gap-4 px-3 py-2 text-sm">
                  <dt className="font-medium text-foreground">{electionStageLabel(o.stage)}</dt>
                  <dd className="flex items-center gap-3">
                    <span>{electionOutcomeResultLabel(o.result)}</span>
                    <span className="text-xs text-muted-foreground">{formatDateTime(o.updatedAt)}</span>
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        {/* Record a stage's outcome */}
        <div className="space-y-4 border-t border-border pt-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="election-stage" className={financeFormLabelClass}>
                Stage
              </label>
              <select
                id="election-stage"
                className={`w-full ${financeFormSelectClass}`}
                value={stage}
                onChange={(e) => setStage(e.target.value as ElectionStage | '')}
              >
                <option value="">Select a stage…</option>
                {STAGES.map((s) => (
                  <option key={s} value={s}>
                    {electionStageLabel(s)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="election-result" className={financeFormLabelClass}>
                Result
              </label>
              <select
                id="election-result"
                className={`w-full ${financeFormSelectClass}`}
                value={result}
                onChange={(e) => setResult(e.target.value as ElectionOutcomeResult | '')}
              >
                <option value="">Select a result…</option>
                {RESULTS.map((r) => (
                  <option key={r} value={r}>
                    {electionOutcomeResultLabel(r)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {needsRunoffDate && (
            <div>
              <label htmlFor="runoff-date" className={financeFormLabelClass}>
                Runoff date
              </label>
              <Input
                id="runoff-date"
                type="date"
                className="w-auto"
                min={getCampaignTodayString()}
                value={runoffDate}
                onChange={(e) => setRunoffDate(e.target.value)}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                The date of the runoff — recurring gifts stay live through it.
              </p>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Button onClick={handleRecord} disabled={!canSubmit}>
              {record.isPending ? 'Recording…' : 'Record outcome'}
            </Button>
            {record.isSuccess && (
              <span className="text-sm text-muted-foreground" data-testid="record-outcome-result">
                Outcome recorded.
              </span>
            )}
          </div>

          {recordError && <p className="text-sm text-destructive">{recordError}</p>}
        </div>
      </CardContent>
    </Card>
  );
}
